import { useEffect, useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Database, Json } from "@/types/supabase";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { deleteProjectFilesFromS3 } from "@/services/s3Service";

type Category = Database['public']['Tables']['categories']['Row'];

// Projelerden gelen ham veri için tip tanımı
type ProjectDataFromSupabase = {
  id: string;
  title: Json | null;
  description: Json | null;
  category_id: string | null;
  order_num: number | null;
  categories: null | { name_tr: string; name_en: string; name_ar: string; };
  content: string | null; 
  created_at: string;
  updated_at: string | null;
};

type Project = Database['public']['Tables']['projects']['Row'] & {
  categoryName?: string;
  mediaCount: number;
  order_num: number; // Bu, projects.Row içinde order_num yoksa veya override etmek için kalabilir
};

interface ProjectWithCategory extends Omit<Project, 'title' | 'description' | 'id' | 'category_id' | 'content' | 'created_at' | 'updated_at' | 'order_num'> {
  // ProjectDataFromSupabase'den gelen ve işlenmiş alanlar
  id: string;
  title: { tr: string; en: string; ar: string; };
  description: { tr: string; en: string; ar: string; };
  category_id: string | null;
  order_num: number;
  categories?: { name_tr: string; name_en: string; name_ar: string; }; // Bu categories ProjectDataFromSupabase'den gelir
  categoryName?: string; // Bu hesaplanır
  mediaCount: number;   // Bu hesaplanır
  content: string | null;
  created_at: string;
  updated_at: string;
}

export default function Projects() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [allProjects, setAllProjects] = useState<ProjectWithCategory[]>([]);
  const [projects, setProjects] = useState<ProjectWithCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('order_num', { ascending: true })
          .returns<Category[]>(); 

        if (categoriesError) {
          console.error('Error fetching categories:', categoriesError);
          toast({ title: "Kategori Yükleme Hatası", description: categoriesError.message, variant: "destructive" });
          setCategories([]);
        } else {
          const validCategories = (categoriesData || []).filter(
            (cat): cat is Category => cat && typeof cat.id === 'string' && cat.id !== ''
          );
          setCategories(validCategories);
        }
        
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select(`
            id, 
            title,
            description,
            category_id,
            order_num,
            content, 
            created_at, 
            updated_at,
            categories:category_id (name_tr, name_en, name_ar)
          `)
          .order('order_num', { ascending: true })
          .returns<ProjectDataFromSupabase[]>(); 
        
        if (projectsError) {
          console.error('Error fetching projects:', projectsError);
          toast({ title: "Proje Yükleme Hatası", description: projectsError.message, variant: "destructive" });
          setAllProjects([]);
        } else {
          const projectsWithMediaPromises = (projectsData || []).map(async (project) => {
            const { count, error: mediaError } = await supabase
              .from('project_media')
              .select('id', { count: 'exact' })
              .eq('project_id', project.id);
            
            if (mediaError) throw mediaError;

            let parsedTitle: ProjectWithCategory['title'] = { tr: '', en: '', ar: '' };
            if (project.title && typeof project.title === 'object' && !Array.isArray(project.title)) {
                parsedTitle = project.title as ProjectWithCategory['title'];
            } else if (typeof project.title === 'string') {
              try {
                const parsed = JSON.parse(project.title);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'tr' in parsed && 'en' in parsed && 'ar' in parsed) {
                    parsedTitle = parsed as ProjectWithCategory['title'] ;
                }
              } catch (e) {
                console.error('Failed to parse project title:', project.title, e);
              }
            }

            let parsedDescription: ProjectWithCategory['description'] = { tr: '', en: '', ar: '' };
            if (project.description && typeof project.description === 'object' && !Array.isArray(project.description)) {
                parsedDescription = project.description as ProjectWithCategory['description'];
            } else if (typeof project.description === 'string') {
              try {
                const parsed = JSON.parse(project.description);
                 if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'tr' in parsed && 'en' in parsed && 'ar' in parsed) {
                    parsedDescription = parsed as ProjectWithCategory['description'];
                }
              } catch (e) {
                console.error('Failed to parse project description:', project.description, e);
              }
            }
            
            return {
              id: project.id,
              category_id: project.category_id,
              content: project.content,
              created_at: project.created_at,
              updated_at: project.updated_at,
              order_num: project.order_num || 0,
              title: parsedTitle,
              description: parsedDescription,
              categories: project.categories,
              categoryName: project.categories?.name_tr || 'Kategorisiz',
              mediaCount: count || 0,
            } as ProjectWithCategory;
          });
          
          const resolvedProjectsWithMedia = await Promise.all(projectsWithMediaPromises);
          setAllProjects(resolvedProjectsWithMedia);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Veri yükleme hatası",
          description: (error as Error).message || "Veriler yüklenirken bilinmeyen bir hata oluştu.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [toast]);

  useEffect(() => {
    if (selectedCategoryId === "all" || selectedCategoryId === "") {
      setProjects([...allProjects].sort((a, b) => a.order_num - b.order_num));
    } else {
      setProjects(
        [...allProjects]
          .filter(project => project.category_id === selectedCategoryId)
          .sort((a, b) => a.order_num - b.order_num)
      );
    }
  }, [selectedCategoryId, allProjects]);

  const fetchDataAfterUpdate = async () => {
    try {
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id, 
          title,
          description,
          category_id,
          order_num,
          content,
          created_at,
          updated_at,
          categories:category_id (name_tr, name_en, name_ar)
        `)
        .order('order_num', { ascending: true })
        .returns<ProjectDataFromSupabase[]>();
      
      if (projectsError) {
        console.error('Error refetching projects:', projectsError);
        toast({ title: "Veri Yenileme Hatası", description: projectsError.message, variant: "destructive" });
      } else {
        const projectsWithMediaPromises = (projectsData || []).map(async (project) => {
            const { count, error: mediaError } = await supabase
              .from('project_media')
              .select('id', { count: 'exact' })
              .eq('project_id', project.id);
            
            if (mediaError) throw mediaError;

            let parsedTitle: ProjectWithCategory['title'] = { tr: '', en: '', ar: '' };
            if (project.title && typeof project.title === 'object' && !Array.isArray(project.title)) {
                parsedTitle = project.title as ProjectWithCategory['title'];
            } else if (typeof project.title === 'string') {
              try {
                const parsed = JSON.parse(project.title);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'tr' in parsed && 'en' in parsed && 'ar' in parsed) {
                    parsedTitle = parsed as ProjectWithCategory['title'] ;
                }
              } catch (e) { console.error('Failed to parse project title (update):', project.title, e); }
            }

            let parsedDescription: ProjectWithCategory['description'] = { tr: '', en: '', ar: '' };
            if (project.description && typeof project.description === 'object' && !Array.isArray(project.description)) {
                parsedDescription = project.description as ProjectWithCategory['description'];
            } else if (typeof project.description === 'string') {
              try {
                const parsed = JSON.parse(project.description);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'tr' in parsed && 'en' in parsed && 'ar' in parsed) {
                    parsedDescription = parsed as ProjectWithCategory['description'];
                }
              } catch (e) { console.error('Failed to parse project description (update):', project.description, e); }
            }
            
            return {
              id: project.id,
              category_id: project.category_id,
              content: project.content,
              created_at: project.created_at,
              updated_at: project.updated_at,
              order_num: project.order_num || 0,
              title: parsedTitle,
              description: parsedDescription,
              categories: project.categories,
              categoryName: project.categories?.name_tr || 'Kategorisiz',
              mediaCount: count || 0
            } as ProjectWithCategory;
        });
        
        const resolvedProjectsWithMedia = await Promise.all(projectsWithMediaPromises);
        setAllProjects(resolvedProjectsWithMedia);
      }
    } catch (error) {
      console.error('Error refetching data:', error);
      toast({ title: "Veri yenileme hatası", description: (error as Error).message || "Veriler güncellenirken bilinmeyen bir hata oluştu.", variant: "destructive" });
    }
  };

  const updateProjectOrder = async (projectId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ order_num: newOrder })
        .eq('id', projectId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating project order:', error);
      toast({ title: "Güncelleme hatası", description: (error as Error).message, variant: "destructive" });
      throw error;
    }
  };

  const moveProject = async (projectId: string, direction: 'up' | 'down') => {
    const projectsToReorder = [...projects];
    const currentIndex = projectsToReorder.findIndex(p => p.id === projectId);
    if (currentIndex === -1) return;

    const currentProject = projectsToReorder[currentIndex];
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= projectsToReorder.length) return;

    const targetProject = projectsToReorder[targetIndex];
    
    try {
      await updateProjectOrder(currentProject.id, targetProject.order_num);
      await updateProjectOrder(targetProject.id, currentProject.order_num);
      await fetchDataAfterUpdate();
      toast({ title: "Sıralama güncellendi", description: "Proje sıralaması başarıyla güncellendi."});
    } catch (error) {
      console.error("Error during moveProject operations:", error);
    }
  };

  const deleteProject = async (id: string) => {
    try {
      // Önce projeye ait medya dosyalarını al
      const { data: mediaData, error: mediaError } = await supabase
        .from('project_media')
        .select('storage_path')
        .eq('project_id', id);
      
      if (mediaError) throw mediaError;
      
      // Projeyi veritabanından sil
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // S3'den dosyaları sil - eğer dosyalar varsa
      if (mediaData && mediaData.length > 0) {
        const filePaths = mediaData.map(media => media.storage_path);
        try {
          await deleteProjectFilesFromS3(id, filePaths);
        } catch (s3Error) {
          console.error('S3 dosya silme hatası:', s3Error);
          // S3 silme hatası olsa bile işleme devam et
        }
      }
      
      await fetchDataAfterUpdate();
      
      toast({
        title: "Proje silindi",
        description: "Proje ve ilişkili tüm dosyalar başarıyla silindi.",
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Silme işlemi başarısız",
        description: (error as Error).message,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <ContentLayout 
        title="Projeler" 
        backLink="/dashboard"
        actionButton={{
          label: "Yeni Proje",
          href: "/portfolio/projects/new"
        }}
      >
        <div className="flex justify-center p-8">
          <p>Projeler yükleniyor...</p>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout 
      title="Projeler" 
      backLink="/dashboard"
      actionButton={{
        label: "Yeni Proje",
        href: "/portfolio/projects/new"
      }}
    >
      <div className="mb-4 p-4 bg-card rounded-lg shadow">
        <Label htmlFor="category-filter" className="text-sm font-medium">Kategoriye Göre Filtrele:</Label>
        <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
          <SelectTrigger id="category-filter" className="w-full md:w-1/3 mt-1">
            <SelectValue placeholder="Kategori seçin..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name_tr}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {projects.length === 0 ? (
        <div className="text-center p-8">
          <p>Henüz proje bulunmuyor.</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Proje Adı</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead className="text-center">Medya Sayısı</TableHead>
              {selectedCategoryId !== "all" && <TableHead className="text-center">Sıralama</TableHead>}
              <TableHead className="text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.title.tr}</TableCell>
                <TableCell>{project.categoryName}</TableCell>
                <TableCell className="text-center">{project.mediaCount}</TableCell>
                {selectedCategoryId !== "all" && (
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => moveProject(project.id, 'up')} disabled={projects.findIndex(p=>p.id === project.id) === 0}>
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => moveProject(project.id, 'down')} disabled={projects.findIndex(p=>p.id === project.id) === projects.length - 1}>
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="icon" asChild>
                      <a href={`/portfolio/projects/${project.id}/edit`}>
                        <Pencil className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteProject(project.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </ContentLayout>
  );
}
